import {
  ChakraHStack,
  FAIconName,
  FireflyButton,
  FrequencyType,
  GroupClassIllustrations,
  GroupClassListViewCard,
  ModalV2,
  Text,
  useToastMessage,
} from '@equip.health/ui';
import { PatientCalendarEvents } from '~/analytics/patientsCalendarEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';

import { useRegisterPatientOrSupportForGroupClassMutation } from '~/store/services/patients';

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userId: string;
  groupClassExternalId: string;
  groupClassName: string;
  Icon: GroupClassIllustrations;
  userName: string;
}

const TOP_LEVEL_ID = 'admin-user-permissions-tree';
const PatientRegisterConfirmationModal = ({
  groupClassExternalId,
  groupClassName,
  Icon,
  isOpen,
  onClose,
  onSuccess,
  userId,
  userName,
}: RegisterModalProps) => {
  const { createToast } = useToastMessage();
  const [registerUser, { isLoading: isRegisteringUser }] =
    useRegisterPatientOrSupportForGroupClassMutation();
  const { trackEvent } = useAnalytics();
  const handleRegister = async () => {
    trackEvent(
      PatientCalendarEvents.registerUserForGroupConfirmationModalRegisterUserClicked,
      {
        Source: AnalyticsSourceValues.PATIENT_CALENDAR_TAB,
      },
    );
    registerUser({ groupClassExternalId, userId })
      .unwrap()
      .then(() => {
        onSuccess();
        createToast({
          icon: FAIconName.checkRegular,
          id: 'register-patient-success-toast',
          text: 'User registered for group successfully',
          variant: 'success',
        });
      })
      .catch(() => {
        createToast({
          icon: FAIconName.exclamationTriangleRegular,
          id: 'unregister-patient-error-toast',
          text: 'Failed to register user',
          variant: 'critical',
        });
      })
      .finally(() => {
        onClose();
      });
  };

  return (
    <>
      <ModalV2
        footerContent={
          <ChakraHStack justifyContent="space-between" width="100%">
            <FireflyButton
              id={`${TOP_LEVEL_ID}__register-btn`}
              leftIcon={FAIconName.arrowLeftRegular}
              onClick={onClose}
              variant="secondary"
            >
              Back
            </FireflyButton>
            <FireflyButton
              id={`${TOP_LEVEL_ID}__register-btn`}
              isLoading={isRegisteringUser}
              leftIcon={FAIconName.checkRegular}
              onClick={handleRegister}
            >
              Register user
            </FireflyButton>
          </ChakraHStack>
        }
        id={TOP_LEVEL_ID}
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        title="Confirm registration"
      >
        <Text
          id={`${TOP_LEVEL_ID}__warning-text`}
          marginBottom="24px"
          variant="body"
        >
          Are you sure you want to register {userName} for this group?
        </Text>

        <GroupClassListViewCard
          frequency={'Does not repeat' as FrequencyType}
          groupClassExternalId={groupClassExternalId}
          iconName={Icon}
          id={groupClassExternalId}
          title={groupClassName}
        />
      </ModalV2>
    </>
  );
};

export default PatientRegisterConfirmationModal;
